import React, { useEffect, useState } from "react";
import {
  AbstractBenefits,
  AbstractBenefitTier,
  AbstractBenefitPhase,
  AbstractBenefitPhaseEnum,
  PharmacyPreferencesAndFrequencies,
} from "../../../@types";
import {
  AbstractBenefitsTableData,
  CellData,
  Footnote,
  GeneralCost,
  Header,
  getCostGivenPhaseTierPref,
  getTierNameList,
  getValidPhases,
  phaseHasFootnote,
  phaseHasGeneralCost,
  scanFootnotes,
} from "./AbstractBenefitsHelpers";
import AbstractBenefitsTable from "./AbstractBenefitsTable";
import { CONSTANTS as C, useTranslate } from "../../../helpers";

const phaseTranslations = {
  [AbstractBenefitPhaseEnum.initial_coverage]:
    "abstract_benefits.phase.initial_coverage",
  [AbstractBenefitPhaseEnum.coverage_gap]:
    "abstract_benefits.phase.coverage_gap",
  [AbstractBenefitPhaseEnum.catastrophic]:
    "abstract_benefits.phase.catastrophic",
  [AbstractBenefitPhaseEnum.lis]: "abstract_benefits.phase.lis",
  [AbstractBenefitPhaseEnum.platino]: "abstract_benefits.phase.platino",
};

const AbstractBenefitsTableContainer: React.FunctionComponent<{
  pharmacyPreference: PharmacyPreferencesAndFrequencies;
  abstractBenefits: AbstractBenefits;
}> = ({ pharmacyPreference, abstractBenefits }) => {
  const t = useTranslate();
  const [tableData, setTableData] = useState<AbstractBenefitsTableData | null>(
    null
  );

  const validPhases = (abstractBenefits
    ? getValidPhases(abstractBenefits)
    : []) as AbstractBenefitPhaseEnum[];

  const getGeneralCost = (phase: AbstractBenefitPhase): GeneralCost =>
    phase.tiers.reduce(
      (generalCost: GeneralCost, tier: AbstractBenefitTier) => {
        if (
          tier.label ===
          C.abstractBenefitTierNames.COST_SHARE_TIER_GENERIC_DRUGS
        ) {
          generalCost.generic = tier.general || "";
        } else if (
          tier.label ===
          C.abstractBenefitTierNames.COST_SHARE_TIER_BRAND_NAME_DRUGS
        ) {
          generalCost.brand = tier.general || "";
        }
        return generalCost;
      },
      { generic: "", brand: "" }
    );

  const buildHeaders = (
    abstractBenefits: AbstractBenefits,
    footnotes: Footnote[]
  ): Header[] => [
    { heading: "Tiers" },
    ...getValidPhases(abstractBenefits).map((phaseName: string) => ({
      heading: t(phaseTranslations[phaseName]),
      footnoteIdx: phaseHasFootnote(footnotes, phaseName)
        ? footnotes.findIndex(footnote => footnote.phaseName === phaseName) + 1
        : undefined,
    })),
  ];

  const buildRows = (
    abstractBenefits: AbstractBenefits | null,
    pharmacyPreference: PharmacyPreferencesAndFrequencies
  ): CellData[][] => {
    if (abstractBenefits) {
      return getTierNameList(abstractBenefits).map(
        (currentTierName: string, tier_index: number) => {
          // first row item is the tier name
          const tierName: CellData = {
            title: currentTierName
              ? t(C.abstractBenefitTierTranslations[currentTierName])
              : `${t("plan_terms.tier")} ${tier_index + 1}`,
            isGeneric: false,
          };

          const rowData = validPhases.map((phaseName: string) => {
            const showGeneralCost = phaseHasGeneralCost(
              abstractBenefits,
              phaseName as AbstractBenefitPhaseEnum
            );

            if (showGeneralCost) {
              const phase = abstractBenefits[phaseName] || null;

              return {
                isGeneric: true,
                generalCost:
                  phase && tier_index === 0 ? getGeneralCost(phase) : undefined,
              };
            }

            const cost = getCostGivenPhaseTierPref(
              abstractBenefits[phaseName],
              currentTierName,
              pharmacyPreference
            );

            return { isGeneric: false, cost };
          });

          return [tierName, ...rowData];
        }
      );
    }

    return [];
  };

  useEffect(() => {
    if (abstractBenefits) {
      const footnotes = scanFootnotes(abstractBenefits);

      if (pharmacyPreference && footnotes !== null) {
        setTableData({
          headers: buildHeaders(abstractBenefits, footnotes),
          rows: buildRows(
            abstractBenefits,
            pharmacyPreference as PharmacyPreferencesAndFrequencies
          ) || ["nothing here"],
          footnotes,
        });
      }
    }
  }, [pharmacyPreference]);

  return tableData ? <AbstractBenefitsTable tableData={tableData} /> : null;
};

export default AbstractBenefitsTableContainer;
